import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useFormik } from 'formik';

import { getRouteUrl } from '@utils/urls';
import { apiErrorsToFieldErrors } from '@utils/validation';
import { getLocale } from '@utils/locale';

import { COUNTRY_PHONE_PREFIX_SWITZERLAND } from '@common/constants/countries';
import { GENDER_FEMALE } from '@common/constants/genders';
import {
  STEP_CONTACT_INFO,
  STEP_DOCUMENTS,
  STEP_SUBMITTED,
} from '@common/constants/jobs';
import {
  JOBS_APPLICATION_SENT,
  CMS_PAGE,
  JOBS_HOME_URL,
  JOBS_SPONTANEOUS_APPLICATION,
} from '@common/routes';
import Layout from '@common/containers/Layout';
import RouteLayout from '@common/containers/RouteLayout';
import DirtyFormConfirmationModal from '@common/components/DirtyFormConfirmationModal';
import JobApplyForm from '@common/components/JobApplyForm';
import {
  jobApplyFormFieldsMap,
  submitJobApplyForm,
} from '@common/components/JobApplyForm/JobApplyForm.helpers';

import { FormContainer } from './JobsSpontaneousApplication.styled';

export function JobsSpontaneousApplication() {
  const router = useRouter();
  const { t } = useTranslation();
  const locale = getLocale(router.locale);
  const pageUrl = getRouteUrl(locale, JOBS_SPONTANEOUS_APPLICATION);

  const handleSubmit = async (values, formikBag) => {
    try {
      const isLastStep = values.step === STEP_DOCUMENTS;
      const isSpontaneous = true;
      const data = await submitJobApplyForm(
        {
          ...values,
          language: locale,
        },
        isLastStep,
        isSpontaneous,
      );

      if (data.step === STEP_SUBMITTED) {
        formikBag.resetForm({ values });
        router.push(
          JOBS_APPLICATION_SENT,
          getRouteUrl(locale, JOBS_APPLICATION_SENT),
        );

        return;
      }

      if (data) {
        formikBag.setValues({ ...values, ...data });
      }
    } catch (error) {
      if (error?.response?.data) {
        const fieldsErrors = apiErrorsToFieldErrors(
          error.response.data,
          jobApplyFormFieldsMap,
        );

        formikBag.setErrors(fieldsErrors);
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      acknowledgement: false,
      closedForAlteration: false,
      confirmEmail: '',
      email: '',
      firstName: '',
      gender: GENDER_FEMALE,
      lastName: '',
      mobilePhone: COUNTRY_PHONE_PREFIX_SWITZERLAND,
      otherJobOffers: [],
      phoneNumber: '',
      phonePrefix: COUNTRY_PHONE_PREFIX_SWITZERLAND,
      primaryJobOffer: null,
      step: STEP_CONTACT_INFO,
      uuid: null,
    },
    onSubmit: handleSubmit,
    validationSchema: JobApplyForm.validationSchema,
  });

  return (
    <Layout
      title={t('jobs.spontaneousApplication.metaTitle', 'Spontanbewerbung')}
      canonicalPageUrl={pageUrl}
    >
      <RouteLayout
        backRoute={CMS_PAGE}
        backRouteAs={`/${locale}${JOBS_HOME_URL[locale]()}`}
        columns={8}
        stickyTitle={t('jobs.spontaneousApplication.title', 'Spontanbewerbung')}
        title={t('jobs.spontaneousApplication.title', 'Spontanbewerbung')}
      >
        <FormContainer>
          <JobApplyForm formik={formik} isSpontaneous />
        </FormContainer>

        <DirtyFormConfirmationModal isDirty={formik.dirty} />
      </RouteLayout>
    </Layout>
  );
}

export default JobsSpontaneousApplication;
